import { timestamp } from '@/api/tripply/tools'
import { Route, RouteGeometry, RouteLogged, LiveData, User, Subscription } from '../models'
import { Feedback } from '../models/Feedback'

// Refs
import { feedbackRef } from './refs/feedback'
import { routeRef, routesRef } from './refs/route'
import { geometryRef } from './refs/route-geometry'
import { routesLogRef } from './refs/routes_log'
import { subscriptionRef } from './refs/subscription'
import { userRef } from './refs/user'

// Create, read, update and delete

interface DatabaseMethods {
  createRoute(route: Route): Promise<string>;
  getRoute(routeId: string): Promise<Route>;
  updateRoute(routeId: string, update: Partial<Route>): Promise<void>;
  deleteRoute(routeId: string): Promise<void>;
  getUserRoutes(userId: string): Promise<Route[]>;
}

type DeepUpdate = {[key: string]: any}

export class DatabaseConnector implements DatabaseMethods {
  async createRoute(route: Route) {
    await routeRef(route.id).set({...route})
    return route.id
  }
  async getRouteGeometry(routeId: string): Promise<RouteGeometry> {
    const geometrySnap = await geometryRef(routeId).get()
    const geometry = geometrySnap.data() || { legs: [] }
    return geometry
  }
  async getRoute(routeId: string): Promise<Route> {
    const routeSnap = await routeRef(routeId).get()
    if (!routeSnap.exists) throw new Error('Route does not exist')
    const route = routeSnap.data() as Route

    // Get route geometry
    const geometry = await this.getRouteGeometry(routeId)

    return {
      ...route,
      id: routeSnap.id,
      geometry: geometry,
      loading: false
    }
  }
  async updateRoute(routeId: string, update: Partial<Route>) {
    const rawUpdate = { ...update }
    if (rawUpdate.geometry) {
      geometryRef(routeId).set(rawUpdate.geometry)
      delete rawUpdate.geometry
    }
    delete rawUpdate.loading
    return routeRef(routeId).update(rawUpdate)
  }
  async deleteRoute(routeId: string) {
    return routeRef(routeId).delete()
  }
  async getUserRoutes(userId: string) {
    const snaps = await routesRef.where('roles.owner', '==', userId).orderBy('timestamps.updated','desc').get()
    return snaps.docs.map(doc => doc.data())
  }
  async createLiveData(routeId: string, liveData: LiveData) {
    return routesRef.doc(routeId).collection('data').doc('live').set(liveData)
  }
  async updateLiveData(routeId: string, liveData: Partial<LiveData>) {
    return routesRef.doc(routeId).collection('data').doc('live').update(liveData)
  }
  async deleteLiveData(routeId: string) {
    return routesRef.doc(routeId).collection('data').doc('live').delete()
  }
  onLiveData(routeId: string, cb: (liveData: LiveData) => void) {
    return routesRef.doc(routeId).collection('data').doc('live').onSnapshot(snap => {
      cb(snap.data() as LiveData)
    })
  }
  async createLoggedRoute(loggedRoute: RouteLogged) {
    return routesLogRef.doc(loggedRoute.id).set(loggedRoute)
  }
  onUser(userId: string, cb: (user: User) => void) {
    return userRef(userId).onSnapshot(snap => {
      cb(snap.data() as User)
    })
  }
  async updateUser(userId: string, user: Partial<User> | DeepUpdate) {
    return userRef(userId).update(user)
  }
  async createFeedback(feedback: Feedback) {
    return feedbackRef.add(feedback)
  }
  onSubscription(userId: string, cb: (subscription: Subscription) => void) {
    return subscriptionRef(userId).onSnapshot(snap => {
      cb(snap.data() || Subscription())
    })
  }
}

export const DB = new DatabaseConnector